import { useResource } from '@landsbankinn/l16n';
import { Box, Button, Flex, H2, P } from '@landsbankinn/ui-kit';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ExternalAuthTypesLink } from 'src/components/Login/Form/ExternalAuthTypesLink.tsx';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  type LoginServerState,
  useApplicationClientContext,
  useApplicationServerContext,
} from '../../../state';
import { ButtonContainer } from '../../Layout';
import { getPreviousLoginType } from '../utils';
import { res } from './LoginError.res';
import { CancelForm } from './LoginForm';

export function LoginError() {
  const { r } = useResource();
  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const [state, dispatch] = useApplicationClientContext();
  const { isMobile, isXL } = useScreenSize();
  const previousLoginType = getPreviousLoginType();
  const navigate = useNavigate();

  const [title, setTitle] = useState<string>(
    r(serverState.isConsentFlow ? res.title_consent : res.title),
  );
  const [description, setDescription] = useState<string>('');
  const [canRetry, setCanRetry] = useState<boolean>(
    serverState.isConsentFlow ? false : true,
  );

  useEffect(() => {
    if (state.errors && state.errors.length > 0 && state.errors[0]) {
      if (state.errorStatus === 'Forbidden') {
        setTitle(r(res.title_forbidden));
      }
      if (state.errorRetryAction === 'Retry') {
        setCanRetry(true);
      }
      if (state.errorRetryAction === 'Cancel') {
        setCanRetry(false);
      }
      setDescription(state.errors.toString());
    } else if (previousLoginType) {
      setDescription(r(res[`description_${previousLoginType}`]));
    }
  }, [
    state.errors,
    state.errorStatus,
    previousLoginType,
    r,
    state.errorRetryAction,
  ]);

  return (
    <Flex
      id="444"
      direction="col"
      mainAxisAlignment="start"
      color="blue font"
      height="100%"
      basis={isXL ? '580' : '100%'}
      grow="0"
    >
      <Flex
        direction="col"
        crossAxisAlignment="center"
        mainAxisAlignment={isMobile ? 'start' : 'center'}
        textAlign="center"
        grow="1"
        paddingTop={isMobile ? '100' : undefined}
      >
        <Box paddingBottom="28">
          <H2
            fontVariant={isMobile ? 'h3' : 'h2'}
            fontSize={isMobile ? '32' : '56'}
          >
            {title}
          </H2>
        </Box>
        <Box paddingBottom="48">
          <P fontVariant="body">{description}</P>
        </Box>
        {state.errorStatus === 'Forbidden' && (
          <Box width="100%">
            <ExternalAuthTypesLink />
          </Box>
        )}
      </Flex>
      <ButtonContainer>
        {!canRetry &&
          previousLoginType !== null &&
          serverState.forms[previousLoginType] && (
            <CancelForm
              form={serverState.forms[previousLoginType]}
              children={(submitCancel) => {
                return (
                  <Button variant="secondary" onClick={submitCancel}>
                    {r(res.close)}
                  </Button>
                );
              }}
            />
          )}
        {canRetry && (
          <Button
            variant="secondary"
            onClick={() => {
              dispatch({ type: 'clearServerErrorState' });
              dispatch({
                type: 'setLoginType',
                payload: { loginType: previousLoginType },
              });
              navigate('/login');
            }}
          >
            {r(res.tryagain)}
          </Button>
        )}
      </ButtonContainer>
    </Flex>
  );
}
